import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    CircleCheck,
    Info,
    Lightbulb,
    StickyNote,
    TriangleAlert
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface SkeletonProps {
    className?: string
}

export const Skeleton: FC<SkeletonProps> = ({ className }): ReactNode => {
    return <div className={cn('animate-pulse rounded-md bg-secondary', className)} aria-hidden='true' />
}

interface EmptyStateProps {
    icon: LucideIcon
    title: string
    description?: ReactNode
    actions?: ReactNode
    compact?: boolean
    className?: string
}

export const EmptyState: FC<EmptyStateProps> = ({
    icon: Icon,
    title,
    description,
    actions,
    compact = false,
    className
}): ReactNode => {
    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center text-center',
                compact ? 'gap-3 px-4 py-8' : 'gap-4 rounded-xl border border-dashed border-input bg-card px-6 py-12',
                className
            )}
        >
            <div className={cn('flex items-center justify-center rounded-full bg-primary-soft text-primary', compact ? 'size-12' : 'size-16')}>
                <Icon className={compact ? 'size-5' : 'size-7'} aria-hidden='true' />
            </div>

            <div className='flex max-w-md flex-col gap-1.5'>
                <p className={cn('font-bold text-navy', compact ? 'text-sm' : 'text-h3')}>
                    {title}
                </p>

                {description && (
                    <div className='text-small text-muted-foreground'>
                        {description}
                    </div>
                )}
            </div>

            {actions && (
                <div className='flex flex-wrap items-center justify-center gap-2'>
                    {actions}
                </div>
            )}
        </div>
    )
}

export type CalloutTone = 'info' | 'tip' | 'success' | 'warning' | 'note'

const CALLOUTS: Record<CalloutTone, { icon: LucideIcon, box: string, icon_: string }> = {
    info: { icon: Info, box: 'border-primary/25 bg-primary-soft/70', icon_: 'text-primary-strong' },
    tip: { icon: Lightbulb, box: 'border-success/25 bg-success-soft/70', icon_: 'text-success-strong' },
    success: { icon: CircleCheck, box: 'border-success/25 bg-success-soft/70', icon_: 'text-success-strong' },
    warning: { icon: TriangleAlert, box: 'border-warning/35 bg-warning-soft/80', icon_: 'text-warning-strong' },
    note: { icon: StickyNote, box: 'border-border bg-secondary/70', icon_: 'text-muted-foreground' }
}

interface CalloutProps {
    tone?: CalloutTone
    title?: string
    children: ReactNode
    className?: string
}

export const Callout: FC<CalloutProps> = ({
    tone = 'info',
    title,
    children,
    className
}): ReactNode => {
    const config = CALLOUTS[tone]
    const Icon = config.icon

    return (
        <div className={cn('flex gap-3 rounded-lg border p-4', config.box, className)}>
            <Icon className={cn('mt-1 size-5 shrink-0', config.icon_)} aria-hidden='true' />

            <div className='flex min-w-0 flex-1 flex-col gap-1 text-body text-foreground'>
                {title && (
                    <p className='font-bold text-navy'>
                        {title}
                    </p>
                )}

                {children}
            </div>
        </div>
    )
}
